import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Star, Copy, Nfc, ShieldCheck, Clock, Wallet as WalletIcon, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pushSupported, pushStatus, enablePush } from "@/lib/push";
import { TiplymeLogo } from "@/components/Brand";
import { Skeleton } from "@/components/ui/skeleton";

function LanyardCard({ worker, card, payLink }) {
  return (
    <div className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-b from-brand-navy to-[#0a1526] p-6 text-white shadow-2xl" data-testid="lanyard-card">
      <div className="grain absolute inset-0 opacity-[0.08]" />
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-cyan/20 blur-2xl" />

      <div className="relative flex items-center justify-between">
        <TiplymeLogo dark />
        <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-brand-cyan"><Nfc className="h-3.5 w-3.5" /> Tap or Scan</span>
      </div>

      <div className="relative mt-5 flex items-center gap-4">
        {worker.photo
          ? <img src={worker.photo} alt={worker.name} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-brand-cyan/40" data-testid="lanyard-photo" />
          : <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/10 font-heading text-2xl font-bold text-brand-cyan">{worker.name?.[0]}</div>}
        <div className="min-w-0">
          <p className="truncate font-heading text-xl font-extrabold" data-testid="lanyard-name">{worker.name}</p>
          <p className="truncate text-sm text-white/70" data-testid="lanyard-place">{worker.place_of_work || worker.workplace_name || "Gig Worker"}</p>
          <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-brand-cyan" data-testid="lanyard-rating">
            <Star className="h-4 w-4 fill-brand-cyan" /> {worker.rating_avg || "New"}
            {worker.rating_count > 0 && <span className="font-normal text-white/50">({worker.rating_count})</span>}
          </p>
        </div>
      </div>

      {card ? (
        <>
          <div className="relative mx-auto mt-6 w-fit rounded-2xl bg-white p-4 shadow-lg" data-testid="lanyard-qr">
            <QRCode value={payLink} size={188} fgColor="#101F38" bgColor="#FFFFFF" level="M" style={{ height: "188px", width: "188px" }} />
          </div>
          <p className="relative mt-4 text-center font-heading text-xl font-bold tracking-tight">Scan or Tap to Tip</p>
          <button
            onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Payment link copied"); }}
            data-testid="lanyard-copy-link"
            className="relative mx-auto mt-3 flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/20">
            <Copy className="h-3.5 w-3.5" /> {card.code}
          </button>
        </>
      ) : (
        <div className="relative mt-6 rounded-2xl border border-white/15 bg-white/5 p-6 text-center" data-testid="lanyard-pending">
          <Clock className="mx-auto h-8 w-8 text-brand-cyan" />
          <p className="mt-3 font-heading font-bold">Awaiting approval</p>
          <p className="mt-1 text-sm text-white/60">Your TiplyMe pay QR code will appear here once your account is approved.</p>
        </div>
      )}

      <div className="relative mt-6 flex flex-col items-center gap-1 border-t border-white/10 pt-4 text-center">
        <span className="font-heading text-sm font-bold text-brand-cyan">tiplyme.co.za</span>
        <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/50">
          <ShieldCheck className="h-3.5 w-3.5" /> Enabled&nbsp;&nbsp;|&nbsp;&nbsp;Secure Digital Micro-Tipping
        </span>
      </div>
    </div>
  );
}

function TipAlertsToggle() {
  const [status, setStatus] = useState("checking");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) { setStatus("unsupported"); return; }
    pushStatus().then(setStatus);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setStatus("on");
      toast.success("Tip alerts on — we'll ping you the moment you're tipped 🎉");
    } catch (e) {
      toast.error(e.message || "Couldn't enable alerts");
      const s = await pushStatus();
      setStatus(s);
    } finally {
      setBusy(false);
    }
  };

  if (status === "unsupported" || status === "checking" || status === "on") return null;

  return (
    <button onClick={enable} disabled={busy} data-testid="enable-tip-alerts"
      className="flex w-full items-center gap-3 rounded-2xl border border-brand-cyan/30 bg-brand-cyan/5 p-4 text-left transition-transform hover:-translate-y-0.5">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-cyan/15 text-brand-cyan"><BellRing className="h-5 w-5" /></span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-foreground">{busy ? "Enabling…" : "Turn on tip alerts"}</span>
        <span className="block text-xs text-muted-foreground">Get an instant notification each time you're tipped.</span>
      </span>
    </button>
  );
}

export default function WorkerDashboard() {
  const [worker, setWorker] = useState(null);
  const [card, setCard] = useState(null);

  useEffect(() => {
    api.get("/worker/profile").then((r) => setWorker(r.data)).catch(() => {});
    api.get("/worker/card").then((r) => setCard(r.data)).catch(() => {});
  }, []);

  if (!worker) return <Skeleton className="h-[520px] w-full rounded-[1.75rem]" />;

  const payLink = card ? `${window.location.origin}/pay/${card.code}` : "";

  return (
    <div className="space-y-4 animate-fade-up" data-testid="worker-dashboard">
      <LanyardCard worker={worker} card={card} payLink={payLink} />
      {card && <TipAlertsToggle />}
      <Link to="/app/wallet" data-testid="home-view-wallet"
        className="flex items-center justify-center gap-2 rounded-2xl border border-border/50 bg-card p-4 text-sm font-semibold text-primary shadow-sm transition-transform hover:-translate-y-0.5">
        <WalletIcon className="h-4 w-4" /> View wallet &amp; earnings
      </Link>
    </div>
  );
}
